import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import ImageDropZone from "./ImageDropZone";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";

interface Banner {
  id: string;
  slot: string;
  title: string | null;
  subtitle: string | null;
  image_url: string | null;
  cta_label: string | null;
  cta_href: string | null;
  is_active: boolean;
  position: number;
}

const KNOWN_SLOTS = [
  { value: "guide_hero", label: "Guide — bannière haut de page" },
  { value: "home_promo", label: "Accueil — bannière promotionnelle" },
];

const AdminBanners = () => {
  const [items, setItems] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("site_banners")
      .select("*")
      .order("slot", { ascending: true })
      .order("position", { ascending: true });
    if (error) toast.error("Erreur chargement bannières : " + error.message);
    setItems((data || []) as Banner[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addBanner = async () => {
    const { data, error } = await supabase
      .from("site_banners")
      .insert({ slot: "home_promo", title: "Nouvelle bannière", is_active: false, position: 0 })
      .select()
      .single();
    if (error) { toast.error(error.message); return; }
    setItems((p) => [...p, data as Banner]);
    toast.success("Bannière créée");
  };

  const update = (id: string, patch: Partial<Banner>) => {
    setItems((p) => p.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const save = async (b: Banner) => {
    setSavingId(b.id);
    const { error } = await supabase
      .from("site_banners")
      .update({
        slot: b.slot,
        title: b.title,
        subtitle: b.subtitle,
        image_url: b.image_url,
        cta_label: b.cta_label,
        cta_href: b.cta_href,
        is_active: b.is_active,
        position: b.position,
      })
      .eq("id", b.id);
    setSavingId(null);
    if (error) toast.error(error.message);
    else toast.success("Bannière sauvegardée");
  };

  const remove = async (id: string) => {
    if (!confirm("Supprimer cette bannière ?")) return;
    const { error } = await supabase.from("site_banners").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { setItems((p) => p.filter((b) => b.id !== id)); toast.success("Supprimée"); }
  };

  const uploadImage = async (id: string, file: File) => {
    const compressed = await compressToWebP(file, { maxSizeMB: 1 });
    const path = `banner-${id}-${Date.now()}.webp`;
    const { error } = await withUploadTimeout(
      supabase.storage.from("site-images").upload(path, compressed, { upsert: true, contentType: "image/webp" })
    );
    if (error) throw new Error(error.message);
    const { data } = supabase.storage.from("site-images").getPublicUrl(path);
    update(id, { image_url: data.publicUrl });
    toast.success("Image téléversée — n'oubliez pas de sauvegarder");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border border-border bg-card/50 p-4">
        <div>
          <h3 className="font-heading text-lg font-semibold">Gestionnaire de bannières</h3>
          <p className="font-body text-sm text-muted-foreground mt-1">
            Texte, image de fond et bouton d'action pour chaque emplacement du site.
          </p>
        </div>
        <Button onClick={addBanner} className="bg-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle bannière
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm p-4">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-muted-foreground text-sm p-4">Aucune bannière. Cliquez sur « Nouvelle bannière ».</div>
      ) : (
        <div className="grid gap-4">
          {items.map((b) => (
            <Card key={b.id} className="border-border">
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="font-heading text-base">
                  {KNOWN_SLOTS.find((s) => s.value === b.slot)?.label ?? b.slot}
                </CardTitle>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={b.is_active} onCheckedChange={(v) => update(b.id, { is_active: v })} />
                    <span className="text-xs text-muted-foreground">{b.is_active ? "Active" : "Inactive"}</span>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => remove(b.id)} aria-label="Supprimer">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label>Emplacement (slot)</Label>
                    <select
                      className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={b.slot}
                      onChange={(e) => update(b.id, { slot: e.target.value })}
                    >
                      {KNOWN_SLOTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      <option value={b.slot}>{b.slot} (personnalisé)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Titre</Label>
                    <Input value={b.title ?? ""} onChange={(e) => update(b.id, { title: e.target.value })} />
                  </div>
                  <div>
                    <Label>Sous-titre</Label>
                    <Textarea rows={3} value={b.subtitle ?? ""} onChange={(e) => update(b.id, { subtitle: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Texte du bouton</Label>
                      <Input value={b.cta_label ?? ""} onChange={(e) => update(b.id, { cta_label: e.target.value })} />
                    </div>
                    <div>
                      <Label>Lien du bouton</Label>
                      <Input value={b.cta_href ?? ""} onChange={(e) => update(b.id, { cta_href: e.target.value })} placeholder="/#booking ou https://…" />
                    </div>
                  </div>
                  <div>
                    <Label>Ordre</Label>
                    <Input type="number" value={b.position} onChange={(e) => update(b.id, { position: Number(e.target.value) || 0 })} />
                  </div>
                  <Button onClick={() => save(b)} disabled={savingId === b.id} className="bg-primary text-primary-foreground">
                    <Save className="w-4 h-4 mr-2" /> {savingId === b.id ? "Sauvegarde…" : "Sauvegarder"}
                  </Button>
                </div>
                <div>
                  <Label>Image de fond</Label>
                  <ImageDropZone
                    currentUrl={b.image_url ?? undefined}
                    onUpload={(f) => uploadImage(b.id, f)}
                    height="h-56"
                  />
                  <p className="text-xs text-muted-foreground mt-2">Format paysage recommandé (≥1600px de large).</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBanners;